/**
 * Brand Configuration
 *
 * Centralized branding settings for the storefront.
 * Update these values when customizing for a new store.
 *
 * @example
 * ```tsx
 * import { brandConfig } from "@/config/brand";
 *
 * <title>{brandConfig.siteName}</title>
 * <p>© {new Date().getFullYear()} {brandConfig.copyrightHolder}</p>
 * ```
 */

import { type Locale, defaultLocale, isValidLocale } from "@/config/locale";
import { resolveContent } from "@/lib/locale/content-chain";
import { getSalesCountryCopy } from "@/config/sales-country-copy";
import nbMetadata from "./nb-metadata-copy.json";

const COMPANY_NAME = "Euro Lab GbR";

/** The authored per-locale tagline and homepage description. */
type LocalizedBrandCopy = { tagline?: string; description: string };

/**
 * Authored brand copy.
 *
 * `Partial` on purpose: de/en/es/tr have authored tagline and description,
 * while Bokmål has a description only. Other missing locales remain a declared
 * type-level gap instead of a `Record<Locale, …>` assertion hiding them. Both readers resolve through the
 * `brandCopy` policy chain, whose second hop is the language the top-level
 * `tagline` / `description` below are authored in — so a locale without an
 * authored entry is reported rather than silently served English (D-03).
 */
const localizedBrandCopy: Partial<Record<Locale, LocalizedBrandCopy>> = {
	de: {
		tagline: "Forschungspeptide von Eurolabs Peptides.",
		description:
			"Eurolabs Peptides ist der Shop der Euro Lab GbR für Forschungsprodukte. Informationen zu Inhalt, Stärke und Preis finden Sie auf der jeweiligen Produktseite.",
	},
	en: {
		tagline: "Research peptides from Eurolabs Peptides.",
		description:
			"Eurolabs Peptides is the research products store operated by Euro Lab GbR. Each product page provides details of contents, strength and price.",
	},
	es: {
		tagline: "Péptidos de investigación de Eurolabs Peptides.",
		description:
			"Eurolabs Peptides es la tienda de productos de investigación de Euro Lab GbR. Cada página de producto incluye información sobre el contenido, la cantidad por vial y el precio.",
	},
	tr: {
		tagline: "Eurolabs Peptides araştırma peptitleri.",
		description:
			"Eurolabs Peptides, Euro Lab GbR tarafından işletilen araştırma ürünleri mağazasıdır. Her ürün sayfasında içerik, flakon başına miktar ve fiyat bilgileri bulunur.",
	},
	nb: { description: nbMetadata["brand.description"] },
	et: {
		tagline: "Uurimispeptiidid Eurolabs Peptidesilt.",
		description:
			"Eurolabs Peptides on Euro Lab GbR uurimistoodete pood. Teavet sisu, tugevuse ja hinna kohta leiate vastava toote lehelt.",
	},
	lv: {
		tagline: "Pētniecības peptīdi no Eurolabs Peptides.",
		description:
			"Eurolabs Peptides ir Euro Lab GbR pētniecības produktu veikals. Informāciju par saturu, stiprumu un cenu Jūs atradīsiet attiecīgajā produkta lapā.",
	},
	fi: {
		tagline: "Tutkimuspeptidejä – Eurolabs Peptides.",
		description:
			"Eurolabs Peptides on Euro Lab GbR:n tutkimustuotteiden verkkokauppa. Tiedot sisällöstä, vahvuudesta ja hinnasta löydätte kunkin tuotteen sivulta.",
	},
	hr: {
		tagline: "Peptidi za istraživanje tvrtke Eurolabs Peptides.",
		description:
			"Eurolabs Peptides je trgovina tvrtke Euro Lab GbR za proizvode za istraživanje. Informacije o sadržaju, jakosti i cijeni potražite na odgovarajućoj stranici proizvoda.",
	},
	hu: {
		tagline: "Kutatási peptidek az Eurolabs Peptides kínálatából.",
		description:
			"Az Eurolabs Peptides az Euro Lab GbR kutatási termékeket kínáló webáruháza. A termék tartalmára, erősségére és árára vonatkozó információkat az adott termékoldalon találja.",
	},
	ro: {
		tagline: "Peptide de cercetare de la Eurolabs Peptides.",
		description:
			"Eurolabs Peptides este magazinul Euro Lab GbR pentru produse de cercetare. Informații despre conținut, concentrație și preț găsiți pe pagina produsului respectiv.",
	},
	sl: {
		tagline: "Raziskovalni peptidi znamke Eurolabs Peptides.",
		description:
			"Eurolabs Peptides je trgovina podjetja Euro Lab GbR za raziskovalne izdelke. Informacije o vsebini, jakosti in ceni najdete na strani posameznega izdelka.",
	},
	el: {
		tagline: "Ερευνητικά πεπτίδια από την Eurolabs Peptides.",
		description:
			"Η Eurolabs Peptides είναι το κατάστημα της Euro Lab GbR για ερευνητικά προϊόντα. Πληροφορίες για το περιεχόμενο, την περιεκτικότητα και την τιμή θα βρείτε στη σελίδα του εκάστοτε προϊόντος.",
	},
	mt: {
		tagline: "Peptidi tar-riċerka minn Eurolabs Peptides.",
		description:
			"Eurolabs Peptides huwa l-ħanut tal-Euro Lab GbR għall-prodotti tar-riċerka. Tistgħu ssibu l-informazzjoni dwar il-kontenut, il-potenza u l-prezz fil-paġna tal-prodott rispettiva.",
	},
	sk: {
		tagline: "Výskumné peptidy od Eurolabs Peptides.",
		description:
			"Eurolabs Peptides je obchod spoločnosti Euro Lab GbR s výskumnými produktmi. Informácie o obsahu, sile a cene nájdete na príslušnej produktovej stránke.",
	},
	ga: {
		tagline: "Peiptídí taighde ó Eurolabs Peptides.",
		description:
			"Is é Eurolabs Peptides siopa Euro Lab GbR do tháirgí taighde. Gheobhaidh tú faisnéis faoi inneachar, neart agus praghas ar an leathanach táirge ábhartha.",
	},
	bg: {
		tagline: "Изследователски пептиди от Eurolabs Peptides.",
		description:
			"Eurolabs Peptides е магазинът на Euro Lab GbR за изследователски продукти. Информация за съдържанието, количеството и цената ще намерите на съответната продуктова страница.",
	},
};

export const brandConfig = {
	/** Site name used in titles, metadata, and headers */
	siteName: "Eurolabs Peptides",

	/** Legal entity name for copyright notices */
	copyrightHolder: COMPANY_NAME,

	/** Organization name for structured data (JSON-LD) */
	organizationName: COMPANY_NAME,

	/** Primary contact address for legal pages, JSON-LD, deployment defaults */
	supportEmail: "service@eurolabs-peptides.com",

	/** Default brand name for products without a brand */
	defaultBrand: "Eurolabs Peptides",

	/** Tagline/description for the store (default/fallback) */
	tagline: "Research peptides from Eurolabs Peptides.",

	/** Homepage meta description (default/fallback) */
	description:
		"Eurolabs Peptides is the research products store operated by Euro Lab GbR. Each product page provides details of contents, strength and price.",

	/** Localized tagline and description (a declared `Partial` — see `localizedBrandCopy`) */
	localized: localizedBrandCopy,

	/** Logo aria-label for accessibility */
	logoAriaLabel: "Eurolabs Peptides",

	/** Title template - %s will be replaced with page title */
	titleTemplate: "%s | Eurolabs Peptides",

	/**
	 * Short brand ending for product titles that would exceed the SEO title target with the full
	 * template (owner decision K1, 2026-09-26: "… | Eurolabs Peptides", or "| Eurolabs" when tight).
	 * See `productDocumentTitle` in src/lib/seo/product-title.ts.
	 */
	shortTitleTemplate: "%s | Eurolabs",

	/** Social media handles */
	social: {
		/** Twitter/X handle (without @) - set to null to disable */
		twitter: null as string | null,
		/** Instagram handle (without @) - set to null to disable */
		instagram: null as string | null,
		/** Facebook page URL - set to null to disable */
		facebook: null as string | null,
	},
} as const;

export const legalPlaceholder = {
	companyName: COMPANY_NAME,
	ownerName: "Dr. Lena Voß",
	partnerName: "Klaus Strüber",
	addressLines: ["Neue Roßstr. 76", "66121 Saarbrücken Sankt Johann", "Saarland, Deutschland"],
	/** Structured form of `addressLines` for schema.org PostalAddress (JSON-LD). */
	postalAddress: {
		streetAddress: "Neue Roßstr. 76",
		postalCode: "66121",
		addressLocality: "Saarbrücken",
		addressRegion: "Saarland",
		addressCountry: "DE",
	},
} as const;

/** Shared legal footer for configured DE/EN campaign mail, independent of promotion copy. */
export const campaignMailFooterCopy = {
	de: {
		research: "Ausschließlich für Forschungszwecke. Nicht zur Anwendung an Menschen oder Tieren.",
		imprint: "Impressum",
		privacy: "Datenschutz",
	},
	en: {
		research: "For research purposes only. Not for use in humans or animals.",
		imprint: "Legal notice",
		privacy: "Privacy policy",
	},
} as const;

/**
 * Advance-bank-transfer (Vorkasse) beneficiary. Hardcoded (not env) so a client
 * component can import it — Turbopack does not inline NEXT_PUBLIC_* into client
 * bundles. This is the payment recipient only; the legal entity on Impressum /
 * Datenschutz stays `legalPlaceholder` (Euro Lab GbR) and is unaffected.
 */
export const bankTransfer = {
	accountHolder: "Kirk & Epstein Trading Limited",
	iban: "DE82202208000047788363",
	ibanGrouped: "DE82 2022 0800 0047 7883 63",
	bic: "SXPYDEHH",
} as const;

/**
 * Get localized tagline.
 *
 * Resolves through the `brandCopy` policy chain, so the English reference hop is
 * reported when a locale has no authored tagline instead of being served
 * silently (D-03). No new tagline text is authored here: the fallback is the
 * existing top-level string.
 */
export function getLocalizedTagline(locale: Locale = defaultLocale): string {
	if (!brandConfig.localized[locale]?.tagline) {
		const translated = getSalesCountryCopy(locale, "brand.tagline");
		if (translated !== undefined) return translated;
	}
	const resolved = resolveContent("brandCopy", locale, (candidate) => {
		return brandConfig.localized[candidate]?.tagline ?? null;
	});
	return resolved?.value ?? brandConfig.tagline;
}

/** Get localized description. Resolves through the same `brandCopy` chain as the tagline. */
export function getLocalizedDescription(locale: string = defaultLocale): string {
	const requested = isValidLocale(locale) ? locale : defaultLocale;
	if (!brandConfig.localized[requested]?.description) {
		const translated = getSalesCountryCopy(requested, "brand.description");
		if (translated !== undefined) return translated;
	}
	const resolved = resolveContent("brandCopy", requested, (candidate) => {
		return brandConfig.localized[candidate]?.description ?? null;
	});
	return resolved?.value ?? brandConfig.description;
}

/**
 * Helper to format page title using brand template.
 */
export function formatPageTitle(title: string): string {
	return brandConfig.titleTemplate.replace("%s", title);
}

/**
 * Get copyright text with specified year.
 * Use CopyrightText component for dynamic year in Server Components.
 */
export function getCopyrightText(year: number = new Date().getFullYear()): string {
	return `© ${year} ${brandConfig.copyrightHolder}. All rights reserved.`;
}

/** Bestseller product slugs for upsell recommendations in cart drawer */
export const UPSELL_BESTSELLERS = ["ghk-cu-50mg", "retatrutide-5mg", "glow-stack"] as const;

/**
 * Products shown as essential accessories on matching category PDPs and in cart drawer.
 * categoryTriggers must match Saleor category names exactly (case-sensitive).
 */
export const ESSENTIAL_ACCESSORIES = [{ slug: "bac-water-10ml", categoryTriggers: ["Peptides"] }] as const;
